import { html, nothing, svg, type TemplateResult } from "lit";

export type PixelIconName =
	| "clock"
	| "folder"
	| "network"
	| "radio"
	| "settings"
	| "shutdown"
	| "speaker"
	| "star"
	| "window";

type IconPalette = {
	background: string;
	fill: string;
	accent: string;
	detail: string;
};

const DETAILED_ICON_NAMES = new Set<PixelIconName>([
	"radio",
	"settings",
	"window",
]);

function iconViewBox(name: PixelIconName) {
	return DETAILED_ICON_NAMES.has(name) ? "0 0 32 32" : "0 0 16 16";
}

const palettes: Record<PixelIconName, IconPalette> = {
	clock: {
		background: "#ffffff",
		fill: "#d6d6d6",
		accent: "#0a246a",
		detail: "#202020",
	},
	folder: {
		background: "#fff5b0",
		fill: "#f4d76a",
		accent: "#c88f19",
		detail: "#6f4e00",
	},
	network: {
		background: "#ddf0ff",
		fill: "#80c8ff",
		accent: "#0a7fd6",
		detail: "#00476f",
	},
	radio: {
		background: "#f1f1f1",
		fill: "#bcbcbc",
		accent: "#4d9b2d",
		detail: "#151515",
	},
	settings: {
		background: "#e9eef5",
		fill: "#bdcce0",
		accent: "#4b6c91",
		detail: "#26384d",
	},
	shutdown: {
		background: "#fdf0e3",
		fill: "#ffb464",
		accent: "#c83c00",
		detail: "#4b1600",
	},
	speaker: {
		background: "#f6f6f6",
		fill: "#d1d1d1",
		accent: "#4ca34a",
		detail: "#1c1c1c",
	},
	star: {
		background: "#fff4af",
		fill: "#ffd94c",
		accent: "#f29f05",
		detail: "#714b00",
	},
	window: {
		background: "#f7f7f7",
		fill: "#d4d0c8",
		accent: "#0a246a",
		detail: "#3d3d3d",
	},
};

function iconSvg(name: PixelIconName, palette: IconPalette) {
	switch (name) {
		case "clock":
			return svg`
				<rect x="2" y="2" width="12" height="12" rx="1" fill="${palette.background}" />
				<rect x="1" y="1" width="14" height="14" fill="none" stroke="${palette.detail}" />
				<path d="M8 4V8H11" stroke="${palette.accent}" stroke-width="2" fill="none" />
				<rect x="7" y="7" width="2" height="2" fill="${palette.detail}" />
			`;
		case "folder":
			return svg`
				<path d="M1 5H7L8 4H15V14H1Z" fill="${palette.fill}" stroke="${palette.detail}" />
				<path d="M1 5H15V7H1Z" fill="${palette.background}" />
				<path d="M2 7H14V13H2Z" fill="${palette.fill}" />
				<path d="M2 8H13" stroke="${palette.accent}" />
			`;
		case "network":
			return svg`
				<rect x="2" y="3" width="5" height="4" fill="${palette.fill}" stroke="${palette.detail}" />
				<rect x="9" y="9" width="5" height="4" fill="${palette.fill}" stroke="${palette.detail}" />
				<path d="M4.5 7V10H11.5V9" stroke="${palette.accent}" stroke-width="2" fill="none" />
				<rect x="6" y="8" width="4" height="2" fill="${palette.background}" />
			`;
		case "radio":
			return svg`
				<!-- Original 32px tabletop radio: layered like a late-1990s shell icon. -->
				<path d="M8 8L25 2" stroke="#171717" stroke-width="2" />
				<rect x="24" y="1" width="3" height="3" fill="#d7d7d7" stroke="#171717" />
				<rect x="3" y="8" width="27" height="20" fill="#171717" />
				<rect x="4" y="9" width="25" height="18" fill="#bdbdbd" />
				<rect x="5" y="10" width="23" height="2" fill="#f5f5f5" />
				<rect x="5" y="12" width="23" height="2" fill="#8c8c8c" />
				<rect x="6" y="15" width="10" height="7" fill="#18291f" stroke="#2b2b2b" />
				<rect x="7" y="16" width="8" height="1" fill="#63d567" />
				<rect x="8" y="18" width="6" height="1" fill="#9bf09d" />
				<rect x="8" y="20" width="4" height="1" fill="#4ba64f" />
				<circle cx="23" cy="19" r="6" fill="#4a4a4a" stroke="#171717" />
				<circle cx="23" cy="19" r="4" fill="#8f8f8f" stroke="#d9d9d9" />
				<path d="M20 17H26M20 19H26M20 21H26" stroke="#4d4d4d" />
				<rect x="6" y="24" width="11" height="2" fill="#727272" />
				<rect x="18" y="24" width="2" height="2" fill="#d04b3d" stroke="#171717" />
				<rect x="21" y="24" width="2" height="2" fill="#e1c752" stroke="#171717" />
				<rect x="6" y="27" width="5" height="2" fill="#303030" />
				<rect x="22" y="27" width="5" height="2" fill="#303030" />
			`;
		case "settings":
			return svg`
				<!-- Original Windows-era control panel with colored hardware sliders. -->
				<rect x="3" y="3" width="27" height="26" fill="#171717" />
				<rect x="4" y="4" width="25" height="24" fill="#c6c6c6" />
				<rect x="5" y="5" width="23" height="4" fill="#153f8f" />
				<rect x="6" y="6" width="8" height="1" fill="#f7f7f7" />
				<rect x="6" y="11" width="20" height="4" fill="#ededed" stroke="#777777" />
				<rect x="7" y="12" width="3" height="2" fill="#da4a43" />
				<rect x="12" y="12" width="12" height="2" fill="#777777" />
				<rect x="18" y="10" width="3" height="6" fill="#f4f4f4" stroke="#171717" />
				<rect x="6" y="17" width="20" height="4" fill="#ededed" stroke="#777777" />
				<rect x="7" y="18" width="3" height="2" fill="#4b9b4c" />
				<rect x="12" y="18" width="12" height="2" fill="#777777" />
				<rect x="14" y="16" width="3" height="6" fill="#f4f4f4" stroke="#171717" />
				<rect x="6" y="23" width="20" height="3" fill="#ededed" stroke="#777777" />
				<rect x="7" y="24" width="3" height="1" fill="#d9bd43" />
				<rect x="12" y="24" width="12" height="1" fill="#777777" />
				<rect x="21" y="22" width="3" height="5" fill="#f4f4f4" stroke="#171717" />
				<rect x="5" y="28" width="8" height="2" fill="#5f5f5f" />
				<rect x="20" y="28" width="8" height="2" fill="#5f5f5f" />
			`;
		case "shutdown":
			return svg`
				<circle cx="8" cy="8" r="6" fill="${palette.fill}" stroke="${palette.detail}" />
				<path d="M8 2V8" stroke="${palette.detail}" stroke-width="2" />
				<path d="M5 4C3.5 5.25 3 6.5 3 8A5 5 0 0 0 13 8C13 6.5 12.5 5.25 11 4" stroke="${palette.accent}" stroke-width="2" fill="none" />
			`;
		case "speaker":
			return svg`
				<path d="M2 6H5L8 3V13L5 10H2Z" fill="${palette.fill}" stroke="${palette.detail}" />
				<path d="M10 6C11 6.8 11 9.2 10 10" stroke="${palette.accent}" stroke-width="2" fill="none" />
				<path d="M12 4C13.8 5.3 13.8 10.7 12 12" stroke="${palette.detail}" />
			`;
		case "star":
			return svg`
				<path d="M8 1L10 5H15L11 8L13 14L8 10L3 14L5 8L1 5H6Z" fill="${palette.fill}" stroke="${palette.detail}" />
				<path d="M8 3L9 5.5H12L9.75 7.25L10.75 10.5L8 8.75L5.25 10.5L6.25 7.25L4 5.5H7Z" fill="${palette.background}" />
			`;
		case "window":
			return svg`
				<!-- Original CRT desktop icon with a miniature classic application window. -->
				<rect x="3" y="3" width="25" height="20" fill="#171717" />
				<rect x="4" y="4" width="23" height="18" fill="#c6c6c6" />
				<rect x="6" y="6" width="19" height="13" fill="#3d6f9f" stroke="#f4f4f4" />
				<rect x="8" y="8" width="15" height="9" fill="#e8e8e8" />
				<rect x="8" y="8" width="15" height="3" fill="#153f8f" />
				<rect x="9" y="9" width="6" height="1" fill="#ffffff" />
				<rect x="10" y="13" width="5" height="1" fill="#808080" />
				<rect x="10" y="15" width="10" height="1" fill="#b0b0b0" />
				<rect x="23" y="20" width="2" height="1" fill="#53b358" />
				<path d="M12 23H20L21 27H23V29H9V27H11Z" fill="#171717" />
				<path d="M13 23H19L19.5 27H12.5Z" fill="#a6a6a6" />
				<rect x="8" y="29" width="16" height="2" fill="#6d6d6d" stroke="#171717" />
			`;
	}
}

// These are original preview-only pixel silhouettes that avoid copied product icons.
export function renderPixelIcon(
	name: PixelIconName,
	label: string,
	className = "pixel-icon",
): TemplateResult {
	const palette = palettes[name];
	return html`
		<svg
			class=${className}
			viewBox=${iconViewBox(name)}
			width="16"
			height="16"
			role=${label ? "img" : nothing}
			aria-label=${label || nothing}
			aria-hidden=${label ? nothing : "true"}
			focusable="false"
		>
			${iconSvg(name, palette)}
		</svg>
	`;
}
